import * as React from 'react';
// Import props.
import {
	ComparativeBuildingsSummaryProps,
	ComparativePerilsDeductiblesSummaryProps,
	FormPopupConfirmationProps,
	FormCoverageComparisonCommercialProps,
	FormProps,
	ScrollbarProps,
} from '@app/props';

interface UseFormCoverageComparisonCommercialParameters {
	id: string;
	props: FormCoverageComparisonCommercialProps,
}

interface UseFormCoverageComparisonCommercialResults {
	applyFormProps: () => FormProps & ScrollbarProps;
	applyComparativeBuildingsSummaryProps: () => ComparativeBuildingsSummaryProps;
	applyComparativePerilsDeductiblesSummaryProps: () => ComparativePerilsDeductiblesSummaryProps;
	applyRemoveBuildingFormPopupConfirmationProps: () => FormPopupConfirmationProps;
}

const useFormCoverageComparisonCommercial = (params: UseFormCoverageComparisonCommercialParameters): UseFormCoverageComparisonCommercialResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
		};
	};

	/**
	 * Returns props for the comparative buildings summary. 
	 **/
	const applyComparativeBuildingsSummaryProps = (): ComparativeBuildingsSummaryProps => {
		return {
			id: generateIdForComparativeBuildingsSummary(),
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			propertyDomainValuesProps: params.props.propertyDomainValuesProps,
			comparisonBuildingsSummary: params.props.comparisonBuildingsSummary,
			focusToAnyErrorOnMount: doAnyBuildingRequireReview,
			focusToTopOnMount: !doAnyBuildingRequireReview && !doPerilsAndDeductiblesRequireReview,
			entityActionProps: {
				...params.props.entityActionProps,
				onRemoveEntity: removeBuilding,
			},
			areHeadersSticky: true,
		};
	};

	/**
	 * Returns props for the comparative perils deductibles summary. 
	 **/
	const applyComparativePerilsDeductiblesSummaryProps = (): ComparativePerilsDeductiblesSummaryProps => {
		return {
			id: generateIdForComparativePerilsDeductiblesSummary(),
			possiblePerils: params.props.possiblePerils,
			comparisonPerilsDeductibleSummary: params.props.comparisonPerilsDeductiblesSummary,
			onEditPerilsDeductibles: params.props.onEditPerilsDeductibles,
			focusToErrorOnMount: !doAnyBuildingRequireReview && doPerilsAndDeductiblesRequireReview,
		};
	};

	/**
	 * Returns props for the remove building confirmation popup form. 
	 **/
	const applyRemoveBuildingFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openRemoveBuildingConfirmationPopup,
			onClose: cancelConfirmRemoveBuilding,
			onConfirm: confirmRemoveBuilding,
			message: 'Are you sure you want to remove this building?',
		};
	};

	// #endregion exported methods


	// #region private methods

	// #region delete building

	/**
	 *  Starts the remove building process.
	 **/
	const removeBuilding = (buildingGuid: string): void => {
		setOpenRemoveBuildingConfirmationPopup(true);
		setBuildingGuidToBeRemoved(buildingGuid);
	}

	/**
	 * Confirms building remove if user chooses to.
	 **/
	const confirmRemoveBuilding = (): void => {
		const buildingGuid: string | undefined = buildingGuidToBeRemoved;
		if (buildingGuid !== undefined) {
			setOpenRemoveBuildingConfirmationPopup(false);
			setBuildingGuidToBeRemoved(undefined);
			params.props.entityActionProps.onRemoveEntity(buildingGuid);
		}
	}

	/**
	 * Cancels the confirmation prompt for removing a building. 
	 **/
	const cancelConfirmRemoveBuilding = (): void => {
		setOpenRemoveBuildingConfirmationPopup(false);
		setBuildingGuidToBeRemoved(undefined);
	}

	// #endregion delete building

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the comparative buildings summary. 
	 **/
	const generateIdForComparativeBuildingsSummary = (): string => {
		return `${generateIdForForm()}-buildingsSummary`;
	}

	/**
	 * Generates id for the comparative perils deductibles summary. 
	 **/
	const generateIdForComparativePerilsDeductiblesSummary = (): string => {
		return `${generateIdForForm()}-perilsDeductibles`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const doAnyBuildingRequireReview: boolean = React.useMemo(() => {
		return params.props.comparisonBuildingsSummary.comparisonEntitySummaries.some(comparisonBuildingSummary => {
			return comparisonBuildingSummary.requiresReview;
		});
	}, [params.props.comparisonBuildingsSummary.comparisonEntitySummaries]);

	const doPerilsAndDeductiblesRequireReview: boolean = React.useMemo(() => {
		return params.props.comparisonPerilsDeductiblesSummary.requiresReview;
	}, [params.props.comparisonPerilsDeductiblesSummary]);

	const [buildingGuidToBeRemoved, setBuildingGuidToBeRemoved] = React.useState<string | undefined>(undefined);
	const [openRemoveBuildingConfirmationPopup, setOpenRemoveBuildingConfirmationPopup] = React.useState<boolean>(false);

	// #endregion variables

	return {
		applyFormProps,
		applyComparativeBuildingsSummaryProps,
		applyComparativePerilsDeductiblesSummaryProps,
		applyRemoveBuildingFormPopupConfirmationProps,
	};
}

// Export parameters.
export {
	UseFormCoverageComparisonCommercialParameters,
}

// Export results.
export {
	UseFormCoverageComparisonCommercialResults,
}

export default useFormCoverageComparisonCommercial;